import React, { useState, useEffect } from "react";
import Navbar from "./navigation";
import TalentCard from "./talent/components/TalentCard";
import {
  Box,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import api from "./api/api";
import { ENDPOINTS } from "./api/endpoints";
import Footer from "./Footer";
import { useNavigate } from "react-router-dom";
import ReplyDrawer from "./ReplyDrawer";
import PaginationControl from "./utils/PaginationControl";
import AppLoader from "./components/common/AppLoader";
import GlobalNotification from "./components/GlobalNotification";

const Favourites = () => {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [brokenImageIds, setBrokenImageIds] = useState([]);
  const clientId = localStorage.getItem("userId");
  const [pageSize, setPageSize] = useState(50);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isReplyDrawerOpen, setIsReplyDrawerOpen] = useState(false);
      const [userId, setUserId] = useState(null);
const userRole = localStorage.getItem("userRole");

  const handleMessageClick = (talent) => {
    const enriched = {
      ...talent,
      talentName: `${talent.firstName || ""} ${talent.lastName || ""}`.trim(),
      originalMessageId: talent.originalMessageId || "0",
      fromEmail: talent.userEmail,
      talentEmail: talent.userEmail,
    };
    setSelectedMessage(enriched);
    setIsReplyDrawerOpen(true);
  };

  const fetchFavorites = async (page = 1, size = pageSize) => {
    setIsLoading(true);
    setError(null);
    try {
      const url = ENDPOINTS.getFavoriteTalentDetails({
        clientId,
        page,
        pageSize: size,
      });
      const resp = await api.get(url);

      const wrapper = resp.data.data || {};
      const list = wrapper.items || [];
      list.sort((a, b) => new Date(b.favoratedDate) - new Date(a.favoratedDate));

      setFavorites(list);
      setTotalResults(wrapper.totalItems ?? list.length);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch favorite talents.");
      setFavorites([]);
      setTotalResults(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchFavorites(currentPage, pageSize);
    } else {
      setError("Missing user. Please log in again.");
    }
  }, [clientId, currentPage, pageSize]);

     useEffect(() => {
      const id = localStorage.getItem("userId");
      if (id) setUserId(id);
    }, []);

  const handleUnfavorite = async (talentId) => {
    try {
      const form = new FormData();
      form.append("TalentId", talentId);
      form.append("ClientId", clientId);
      form.append("Fave", "false");

      await api.post(ENDPOINTS.addOrUpdateFave(), form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      fetchFavorites(currentPage);
    } catch {
      console.error("Remove favourite failed");
    }
  };

  return (
    <div style={{ position: "relative", minHeight: "100vh" }}>
      <Navbar />

      {/*  Global Loader Overlay */}
      {isLoading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(255,255,255,0.6)",
            zIndex: 1500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AppLoader message="Loading favourite talents..." />
        </div>
      )}

      <Box sx={{ background: "#eeeeee", py: 4 }}>
        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            borderRadius: 2,
            p: 3,
          }}
        >
          <GlobalNotification userId={userId} userRole={userRole} />
          <Typography variant="h4" gutterBottom>
            Favorites
          </Typography>

          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          {/* Controls Row (page size dropdown like ResultsPanel) */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2,
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <FormControl size="small" sx={{ minWidth: 160, background: "#fff" }}>
              <InputLabel>Talents / Page</InputLabel>
              <Select
                value={pageSize}
                label="Talents / Page"
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {[50, 100, 200, 500].map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/*  Responsive auto-fill grid same as ResultsPanel */}
          {!isLoading && favorites.length > 0 ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                gap: 1.5,
                px: 1,
                py: 2,
              }}
            >
              {favorites.map((profile) => (
                <Box key={profile.userId} sx={{ width: "100%" }}>
                  <TalentCard
                    profile={{ ...profile, favorite: true }}
                    brokenImageIds={brokenImageIds}
                    handleFavouriteClick={() => handleUnfavorite(profile.userId)}
                    handleMessageClick={handleMessageClick}
                    navigate={navigate}
                    disableMessageButton={true}
                  />
                </Box>
              ))}
            </Box>
          ) : (
            !isLoading && <Typography>No favorite talents found.</Typography>
          )}

          <PaginationControl
            page={currentPage}
            pageSize={pageSize}
            totalItems={totalResults}
            onPageChange={(newPage) => setCurrentPage(newPage)}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setCurrentPage(1);
            }}
            pageSizeOptions={[50, 150, 500]}
            sx={{ mt: 2 }}
          />
        </Box>
      </Box>

      <Footer />

      <ReplyDrawer
        open={isReplyDrawerOpen}
        onClose={() => setIsReplyDrawerOpen(false)}
        selectedMessage={selectedMessage}
      />
    </div>
  );
};

export default Favourites;
